import { z } from 'zod';
/**
 * Project Zod schemas for API validation
 *
 * These schemas validate API request and response payloads.
 * Used by both backend (validation) and frontend (type inference).
 */
/**
 * Project response schema
 */
export const projectSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    domainUuid: z.string().uuid(),
    workspaceUuid: z.string().uuid(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Create project request schema
 */
export const createProjectRequestSchema = z.object({
    domainUuid: z.string().uuid('Invalid domain UUID'),
    name: z
        .string()
        .min(1, 'Name is required')
        .max(100, 'Name must be 100 characters or less')
        .trim(),
    description: z.string().max(500, 'Description must be 500 characters or less').trim().optional(),
    /**
     * Optional list of workspace member UUIDs to additionally assign as project admins at creation.
     * Domain admins are already inherited automatically via the domain member seed.
     */
    projectAdminUuids: z
        .array(z.string().uuid('Invalid user UUID - must be a valid workspace member'))
        .optional(),
});
/**
 * Update project request schema
 *
 * When projectAdminUuids is provided it replaces the full admin list:
 * new UUIDs are assigned, removed UUIDs are revoked.
 */
export const updateProjectRequestSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(100, 'Name must be 100 characters or less')
        .trim()
        .optional(),
    description: z.string().max(500, 'Description must be 500 characters or less').trim().optional(),
    projectAdminUuids: z
        .array(z.string().uuid('Invalid user UUID - must be a valid workspace member'))
        .optional(),
});
/**
 * List projects response schema
 */
export const listProjectsResponseSchema = z.object({
    projects: z.array(projectSchema),
});
/**
 * Create project response schema
 */
export const createProjectResponseSchema = z.object({
    project: projectSchema,
});
/**
 * Assign project role request schema
 *
 * Used by workspace admins, domain admins, or project admins to assign
 * any project-level role to a workspace member.
 */
export const assignProjectRoleRequestSchema = z.object({
    userUuid: z.string().uuid('Invalid user UUID'),
    role: z.enum(['admin', 'manager', 'viewer', 'collaborator'], {
        errorMap: () => ({ message: 'Role must be one of: admin, manager, viewer, collaborator' }),
    }),
});
// ─── Project Context Documents ────────────────────────────────────────────────
/**
 * Project context document response schema
 */
export const projectContextDocumentSchema = z.object({
    uuid: z.string().uuid(),
    projectUuid: z.string().uuid(),
    title: z.string(),
    body: z.record(z.unknown()).nullable(),
    contentText: z.string().nullable(),
    sourceSystem: z.string().nullable(),
    createdByUserUuid: z.string().uuid(),
    createdByDisplayName: z.string().nullable(),
    updatedByUserUuid: z.string().uuid().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Create context document request
 */
export const createContextDocumentRequestSchema = z.object({
    title: z
        .string()
        .min(1, 'Title is required')
        .max(250, 'Title must be 250 characters or less')
        .trim(),
    body: z.record(z.unknown()).optional(),
    contentText: z.string().optional(),
    sourceSystem: z.string().nullable().optional(),
});
/**
 * Update context document request
 */
export const updateContextDocumentRequestSchema = z.object({
    title: z
        .string()
        .min(1, 'Title is required')
        .max(250, 'Title must be 250 characters or less')
        .trim()
        .optional(),
    body: z.record(z.unknown()).optional(),
    contentText: z.string().optional(),
});
/**
 * List context documents response
 */
export const listContextDocumentsResponseSchema = z.object({
    documents: z.array(projectContextDocumentSchema),
});
//# sourceMappingURL=project.js.map